import { 
  collection, 
  addDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where,
  limit
} from 'firebase/firestore';
import { db } from '../config/firebase';

// Farmers Collection
export const farmersCollection = collection(db, 'farmers');
export const milkEntriesCollection = collection(db, 'milkEntries');
export const amountGivenCollection = collection(db, 'amountGiven');

// Cache for better performance
let farmersCache = null;
let entriesCache = null;
let lastFarmersUpdate = 0;
let lastEntriesUpdate = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Farmer Functions
export const addFarmer = async (farmerData) => {
  try {
    const docRef = await addDoc(farmersCollection, {
      ...farmerData,
      createdAt: new Date(),
      userId: localStorage.getItem('userId')
    });
    const newFarmer = { id: docRef.id, ...farmerData };
    
    // Update cache
    if (farmersCache) {
      farmersCache.push(newFarmer);
    }
    
    return newFarmer;
  } catch (error) {
    console.error('Error adding farmer:', error);
    throw error;
  }
};

export const getFarmers = async (useCache = true) => {
  try {
    const now = Date.now();
    
    // Return cached data if available and fresh
    if (useCache && farmersCache && (now - lastFarmersUpdate) < CACHE_DURATION) {
      return farmersCache;
    }
    
    const userId = localStorage.getItem('userId');
    const loginMode = localStorage.getItem('loginMode');
    const farmerCode = localStorage.getItem('farmerCode');
    
    if (!userId) {
      // Return empty array for unauthenticated users (like on landing page)
      return [];
    }
    
    let q;
    if (loginMode === 'farmer' && farmerCode) {
      // For farmer login, get all farmers but will be filtered later
      q = query(farmersCollection);
    } else {
      // For admin login, get only their farmers
      q = query(farmersCollection, where('userId', '==', userId));
    }
    
    const querySnapshot = await getDocs(q);
    
    let farmers = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // If farmer login, filter to show only the logged-in farmer
    if (loginMode === 'farmer' && farmerCode) {
      farmers = farmers.filter(f => f.code === farmerCode);
    }
    
    // Sort by createdAt on client side
    farmers.sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || new Date(0);
      const bTime = b.createdAt?.toDate?.() || new Date(0);
      return aTime - bTime;
    });
    
    farmersCache = farmers;
    lastFarmersUpdate = now;
    
    return farmersCache;
  } catch (error) {
    console.error('Error getting farmers:', error);
    // Return cached data if available, even if stale, or empty array
    if (farmersCache) {
      return farmersCache;
    }
    return [];
  }
};

// Milk Entry Functions
export const addMilkEntry = async (entryData) => {
  try {
    const docRef = await addDoc(milkEntriesCollection, {
      ...entryData,
      createdAt: new Date(),
      userId: localStorage.getItem('userId')
    });
    const newEntry = { id: docRef.id, ...entryData };
    
    // Update cache
    if (entriesCache) {
      entriesCache.unshift(newEntry);
    }
    
    return newEntry;
  } catch (error) {
    console.error('Error adding milk entry:', error);
    throw error;
  }
};

export const getMilkEntries = async (useCache = true) => {
  try {
    const now = Date.now();
    
    // Return cached data if available and fresh
    if (useCache && entriesCache && (now - lastEntriesUpdate) < CACHE_DURATION) {
      return entriesCache;
    }
    
    const userId = localStorage.getItem('userId');
    const loginMode = localStorage.getItem('loginMode');
    const farmerCode = localStorage.getItem('farmerCode');
    
    if (!userId) {
      // Return empty array for unauthenticated users (like on landing page)
      return [];
    }
    
    let q;
    if (loginMode === 'farmer' && farmerCode) {
      // For farmer login, get entries for this specific farmer only
      q = query(milkEntriesCollection, where('farmerCode', '==', farmerCode));
    } else {
      // For admin login, get all their entries
      q = query(milkEntriesCollection, where('userId', '==', userId));
    }
    
    const querySnapshot = await getDocs(q);
    
    entriesCache = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort by date on client side (most recent first)
    entriesCache.sort((a, b) => {
      const aDate = new Date(a.date || '1970-01-01');
      const bDate = new Date(b.date || '1970-01-01');
      return bDate - aDate;
    });
    
    lastEntriesUpdate = now;
    
    return entriesCache;
  } catch (error) {
    console.error('Error getting milk entries:', error);
    // Return cached data if available, even if stale, or empty array
    if (entriesCache) {
      return entriesCache;
    }
    return [];
  }
};

export const updateMilkEntry = async (entryId, entryData) => {
  try {
    const entryRef = doc(db, 'milkEntries', entryId);
    await updateDoc(entryRef, {
      ...entryData,
      updatedAt: new Date()
    });
    
    const updatedEntry = { id: entryId, ...entryData };
    
    // Update cache
    if (entriesCache) {
      const index = entriesCache.findIndex(e => e.id === entryId);
      if (index !== -1) {
        entriesCache[index] = { ...entriesCache[index], ...entryData };
      }
    }
    
    return updatedEntry;
  } catch (error) {
    console.error('Error updating milk entry:', error);
    throw error;
  }
};

export const deleteMilkEntry = async (entryId) => {
  try {
    await deleteDoc(doc(db, 'milkEntries', entryId));
    
    // Update cache
    if (entriesCache) {
      entriesCache = entriesCache.filter(e => e.id !== entryId);
    }
    
    return entryId;
  } catch (error) {
    console.error('Error deleting milk entry:', error);
    throw error;
  }
};

// Create demo farmer if it doesn't exist
export const createDemoFarmer = async () => {
  try {
    // Check if demo farmer already exists
    const q = query(farmersCollection, where('code', '==', 'F001'));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.docs.length === 0) {
      // Create demo farmer
      const demoFarmer = {
        name: 'Akshay Bellagi',
        code: 'F001',
        mobile: '7676831143',
        village: 'Kakmari',
        createdAt: new Date(),
        userId: 'demo-user-id' // Special demo user ID
      };
      
      console.log('Demo farmer created successfully');
      
      // Create some sample milk entries for the demo farmer
      const today = new Date().toISOString().slice(0, 10);
      const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
      
      const sampleEntries = [
        {
          date: today,
          farmerCode: 'F001',
          animalType: 'Cow',
          time: 'Morning',
          quantity: 20,
          fat: 4.2,
          price: 23,
          createdAt: new Date(),
          userId: 'demo-user-id'
        },
        {
          date: yesterday,
          farmerCode: 'F001',
          animalType: 'Cow',
          time: 'Evening',
          quantity: 18,
          fat: 4.0,
          price: 22,
          createdAt: new Date(),
          userId: 'demo-user-id'
        }
      ];
      
      // Add sample entries
      for (const entry of sampleEntries) {
        await addDoc(milkEntriesCollection, entry);
      }
      
      // Create sample cattle food entries
      const sampleCattleFood = [
        {
          date: today,
          farmerCode: 'F001',
          itemName: 'Cattle Feed',
          quantity: 10,
          rate: 25,
          totalAmount: 250,
          createdAt: new Date(),
          userId: 'demo-user-id'
        },
        {
          date: yesterday,
          farmerCode: 'F001',
          itemName: 'Hay',
          quantity: 5,
          rate: 30,
          totalAmount: 150,
          createdAt: new Date(),
          userId: 'demo-user-id'
        }
      ];
      
      // Add sample cattle food entries
      for (const entry of sampleCattleFood) {
        await addDoc(cattleFoodEntriesCollection, entry);
      }
      
      // Create sample payment entries
      const samplePayments = [
        {
          farmerCode: 'F001',
          amount: 1000,
          paymentMethod: 'Cash',
          notes: 'Weekly payment',
          createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 7 days ago
          userId: 'demo-user-id'
        },
        {
          farmerCode: 'F001',
          amount: 1500,
          paymentMethod: 'Bank Transfer',
          notes: 'Monthly advance',
          createdAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000), // 14 days ago
          userId: 'demo-user-id'
        }
      ];
      
      // Add sample payment entries
      for (const payment of samplePayments) {
        await addDoc(paymentsCollection, payment);
      }
      
      console.log('Demo data created successfully');
    }
  } catch (error) {
    console.error('Error creating demo farmer:', error);
  }
};

// Generate farmer code
export const generateFarmerCode = (farmers) => {
  const nextNum = (farmers.length + 1).toString().padStart(3, '0');
  return `F${nextNum}`;
};

// Cache management functions
export const clearCache = () => {
  farmersCache = null;
  entriesCache = null;
  lastFarmersUpdate = 0;
  lastEntriesUpdate = 0;
};

export const refreshFarmersCache = () => {
  farmersCache = null;
  lastFarmersUpdate = 0;
};

export const refreshEntriesCache = () => {
  entriesCache = null;
  lastEntriesUpdate = 0;
};

// Test function to verify Firebase connection
export const testFirebaseConnection = async () => {
  try {
    const userId = localStorage.getItem('userId');
    console.log('Testing Firebase connection...');
    console.log('User ID:', userId);
    
    if (!userId) {
      throw new Error('No user ID found in localStorage');
    }
    
    // Try to read from farmers collection
    const q = query(farmersCollection, where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    
    console.log('Firebase connection successful!');
    console.log('Found documents:', querySnapshot.size);
    
    return {
      success: true,
      documentsFound: querySnapshot.size,
      userId: userId
    };
  } catch (error) {
    console.error('Firebase connection test failed:', error);
    return {
      success: false,
      error: error.message,
      userId: localStorage.getItem('userId')
    };
  }
};

// Additional collections
export const cattleFoodEntriesCollection = collection(db, 'cattleFoodEntries');
export const customFoodItemsCollection = collection(db, 'customFoodItems');
export const transactionsCollection = collection(db, 'transactions');

// Cache for additional data
let cattleFoodEntriesCache = null;
let customFoodItemsCache = null;
let transactionsCache = null;
let lastCattleFoodEntriesUpdate = 0;
let lastCustomFoodItemsUpdate = 0;
let lastTransactionsUpdate = 0;

// Cattle Food Entry Functions
export const addCattleFoodEntry = async (entryData) => {
  try {
    const docRef = await addDoc(cattleFoodEntriesCollection, {
      ...entryData,
      createdAt: new Date(),
      userId: localStorage.getItem('userId')
    });
    const newEntry = { id: docRef.id, ...entryData };
    
    // Update cache
    if (cattleFoodEntriesCache) {
      cattleFoodEntriesCache.unshift(newEntry);
    }
    
    return newEntry;
  } catch (error) {
    console.error('Error adding cattle food entry:', error);
    throw error;
  }
};

export const getCattleFoodEntries = async (useCache = true) => {
  try {
    const now = Date.now();
    
    if (useCache && cattleFoodEntriesCache && (now - lastCattleFoodEntriesUpdate) < CACHE_DURATION) {
      return cattleFoodEntriesCache;
    }
    
    const userId = localStorage.getItem('userId');
    const loginMode = localStorage.getItem('loginMode');
    const farmerCode = localStorage.getItem('farmerCode');
    
    if (!userId) {
      // Return empty array for unauthenticated users (like on landing page)
      return [];
    }
    
    let q;
    if (loginMode === 'farmer' && farmerCode) {
      // For farmer login, get entries for this specific farmer only
      q = query(cattleFoodEntriesCollection, where('farmerCode', '==', farmerCode));
    } else {
      // For admin login, get all their entries
      q = query(cattleFoodEntriesCollection, where('userId', '==', userId));
    }
    
    const querySnapshot = await getDocs(q);
    
    cattleFoodEntriesCache = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort by date (most recent first)
    cattleFoodEntriesCache.sort((a, b) => {
      const aDate = new Date(a.date || '1970-01-01');
      const bDate = new Date(b.date || '1970-01-01');
      return bDate - aDate;
    });
    
    lastCattleFoodEntriesUpdate = now;
    
    return cattleFoodEntriesCache;
  } catch (error) {
    console.error('Error getting cattle food entries:', error);
    if (cattleFoodEntriesCache) {
      return cattleFoodEntriesCache;
    }
    return [];
  }
};

// Custom Food Items Functions
export const addCustomFoodItem = async (itemData) => {
  try {
    const docRef = await addDoc(customFoodItemsCollection, {
      ...itemData,
      createdAt: new Date(),
      userId: localStorage.getItem('userId')
    });
    const newItem = { id: docRef.id, ...itemData };
    
    // Update cache
    if (customFoodItemsCache) {
      customFoodItemsCache.push(newItem);
    }
    
    return newItem;
  } catch (error) {
    console.error('Error adding custom food item:', error);
    throw error;
  }
};

export const getCustomFoodItems = async (useCache = true) => {
  try {
    const now = Date.now();
    
    if (useCache && customFoodItemsCache && (now - lastCustomFoodItemsUpdate) < CACHE_DURATION) {
      return customFoodItemsCache;
    }
    
    const userId = localStorage.getItem('userId');
    
    if (!userId) {
      // Return empty array for unauthenticated users (like on landing page)
      return [];
    }
    
    const q = query(customFoodItemsCollection, where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    
    // Deduplicate by ID to prevent duplicate key warnings
    const itemsMap = new Map();
    querySnapshot.docs.forEach(doc => {
      const item = { id: doc.id, ...doc.data() };
      itemsMap.set(doc.id, item);
    });
    
    customFoodItemsCache = Array.from(itemsMap.values());
    
    lastCustomFoodItemsUpdate = now;
    
    return customFoodItemsCache;
  } catch (error) {
    console.error('Error getting custom food items:', error);
    if (customFoodItemsCache) {
      return customFoodItemsCache;
    }
    return [];
  }
};

export const deleteCustomFoodItem = async (itemId) => {
  try {
    await deleteDoc(doc(db, 'customFoodItems', itemId));
    
    // Update cache
    if (customFoodItemsCache) {
      customFoodItemsCache = customFoodItemsCache.filter(item => item.id !== itemId);
    }
    
    return itemId;
  } catch (error) {
    console.error('Error deleting custom food item:', error);
    throw error;
  }
};

// Transaction Functions
export const addTransaction = async (transactionData) => {
  try {
    const docRef = await addDoc(transactionsCollection, {
      ...transactionData,
      createdAt: new Date(),
      userId: localStorage.getItem('userId')
    });
    const newTransaction = { id: docRef.id, ...transactionData };
    
    // Update cache
    if (transactionsCache) {
      transactionsCache.unshift(newTransaction);
      // Keep only last 50 transactions
      transactionsCache = transactionsCache.slice(0, 50);
    }
    
    return newTransaction;
  } catch (error) {
    console.error('Error adding transaction:', error);
    throw error;
  }
};

export const getTransactions = async (useCache = true) => {
  try {
    const now = Date.now();
    
    if (useCache && transactionsCache && (now - lastTransactionsUpdate) < CACHE_DURATION) {
      return transactionsCache;
    }
    
    const userId = localStorage.getItem('userId');
    
    if (!userId) {
      throw new Error('User not authenticated - no userId found');
    }
    
    const q = query(transactionsCollection, where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    
    transactionsCache = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort by timestamp (most recent first)
    transactionsCache.sort((a, b) => {
      const aTime = new Date(a.timestamp || a.createdAt?.toDate?.() || '1970-01-01');
      const bTime = new Date(b.timestamp || b.createdAt?.toDate?.() || '1970-01-01');
      return bTime - aTime;
    });
    
    // Keep only last 50 transactions
    transactionsCache = transactionsCache.slice(0, 50);
    
    lastTransactionsUpdate = now;
    
    return transactionsCache;
  } catch (error) {
    console.error('Error getting transactions:', error);
    if (transactionsCache) {
      return transactionsCache;
    }
    throw error;
  }
};

export const deleteTransaction = async (transactionId) => {
  try {
    await deleteDoc(doc(db, 'transactions', transactionId));
    
    // Update cache
    if (transactionsCache) {
      transactionsCache = transactionsCache.filter(t => t.id !== transactionId);
    }
    
    return transactionId;
  } catch (error) {
    console.error('Error deleting transaction:', error);
    throw error;
  }
};

// Amount Given Functions - Per Farmer Payment Tracking
export const paymentsCollection = collection(db, 'farmerPayments');
let paymentsCache = null;
let lastPaymentsUpdate = 0;

export const addFarmerPayment = async (paymentData) => {
  try {
    const userId = localStorage.getItem('userId');
    if (!userId) throw new Error('User not authenticated');
    
    const docRef = await addDoc(paymentsCollection, {
      ...paymentData,
      createdAt: new Date(),
      userId
    });
    
    // Clear cache to force refresh
    paymentsCache = null;
    lastPaymentsUpdate = 0;
    
    return { id: docRef.id, ...paymentData };
  } catch (error) {
    console.error('Error adding farmer payment:', error);
    throw error;
  }
};

export const getFarmerPayments = async (useCache = true) => {
  try {
    const now = Date.now();
    
    if (useCache && paymentsCache && (now - lastPaymentsUpdate) < CACHE_DURATION) {
      return paymentsCache;
    }
    
    const userId = localStorage.getItem('userId');
    const loginMode = localStorage.getItem('loginMode');
    const farmerCode = localStorage.getItem('farmerCode');
    
    if (!userId) {
      // Return empty array for unauthenticated users
      return [];
    }
    
    let q;
    if (loginMode === 'farmer' && farmerCode) {
      // For farmer login, get payments for this specific farmer only
      q = query(paymentsCollection, where('farmerCode', '==', farmerCode));
    } else {
      // For admin login, get all payments they manage
      q = query(paymentsCollection, where('userId', '==', userId));
    }
    
    const querySnapshot = await getDocs(q);
    
    paymentsCache = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort by date (most recent first)
    paymentsCache.sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
      const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
      return bTime - aTime;
    });
    
    lastPaymentsUpdate = now;
    return paymentsCache;
  } catch (error) {
    console.error('Error getting farmer payments:', error);
    if (paymentsCache) return paymentsCache;
    return [];
  }
};

export const getTotalPaymentsForFarmer = (payments, farmerCode) => {
  return payments
    .filter(p => p.farmerCode === farmerCode)
    .reduce((sum, p) => sum + (p.amount || 0), 0);
};

export const deleteFarmerPayment = async (paymentId) => {
  try {
    await deleteDoc(doc(db, 'farmerPayments', paymentId));
    
    // Clear cache
    paymentsCache = null;
    lastPaymentsUpdate = 0;
    
    return paymentId;
  } catch (error) {
    console.error('Error deleting farmer payment:', error);
    throw error;
  }
};

// Milk Prices Collection
export const milkPricesCollection = collection(db, 'milkPrices');
let milkPricesCache = null;
let lastMilkPricesUpdate = 0;

export const getMilkPrices = async (useCache = true) => {
  try {
    const now = Date.now();
    
    if (useCache && milkPricesCache && (now - lastMilkPricesUpdate) < CACHE_DURATION) {
      return milkPricesCache;
    }
    
    const userId = localStorage.getItem('userId');
    
    if (!userId) {
      throw new Error('User not authenticated - no userId found');
    }
    
    const q = query(milkPricesCollection, where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    
    milkPricesCache = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    lastMilkPricesUpdate = now;
    
    return milkPricesCache;
  } catch (error) {
    console.error('Error getting milk prices:', error);
    if (milkPricesCache) {
      return milkPricesCache;
    }
    return [];
  }
};

export const updateMilkPrice = async (priceId, priceData) => {
  try {
    const priceRef = doc(db, 'milkPrices', priceId);
    await updateDoc(priceRef, {
      ...priceData,
      updatedAt: new Date()
    });
    
    // Clear cache
    milkPricesCache = null;
    lastMilkPricesUpdate = 0;
    
    return { id: priceId, ...priceData };
  } catch (error) {
    console.error('Error updating milk price:', error);
    throw error;
  }
};

export const setMilkPrice = async (animalType, time, price) => {
  try {
    const userId = localStorage.getItem('userId');
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    // Check if price already exists
    const q = query(
      milkPricesCollection,
      where('userId', '==', userId),
      where('animalType', '==', animalType),
      where('time', '==', time)
    );
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.docs.length > 0) {
      // Update existing
      const docId = querySnapshot.docs[0].id;
      return await updateMilkPrice(docId, { price: parseFloat(price) });
    } else {
      // Create new
      const docRef = await addDoc(milkPricesCollection, {
        animalType,
        time,
        price: parseFloat(price),
        createdAt: new Date(),
        userId: userId
      });
      
      const newPrice = { id: docRef.id, animalType, time, price: parseFloat(price) };
      if (milkPricesCache) {
        milkPricesCache.push(newPrice);
      }
      
      return newPrice;
    }
  } catch (error) {
    console.error('Error setting milk price:', error);
    throw error;
  }
};

// Update cache clearing function
export const clearAllCache = () => {
  farmersCache = null;
  entriesCache = null;
  cattleFoodEntriesCache = null;
  customFoodItemsCache = null;
  transactionsCache = null;
  milkPricesCache = null;
  paymentsCache = null;
  fatWisePricesCache = null;
  lastFarmersUpdate = 0;
  lastEntriesUpdate = 0;
  lastCattleFoodEntriesUpdate = 0;
  lastCustomFoodItemsUpdate = 0;
  lastTransactionsUpdate = 0;
  lastMilkPricesUpdate = 0;
  lastPaymentsUpdate = 0;
  lastFatWisePricesUpdate = 0;
};

// Make clearAllCache available globally for login
if (typeof window !== 'undefined') {
  window.clearAllCache = clearAllCache;
}


// Fat-wise Prices Collection
export const fatWisePricesCollection = collection(db, 'fatWisePrices');
let fatWisePricesCache = null;
let lastFatWisePricesUpdate = 0;

// Public data cache for landing page
let publicFarmersCache = null;
let publicPricesCache = null;
let lastPublicDataUpdate = 0;

export const getFatWisePrices = async (milkType, useCache = true) => {
  try {
    const now = Date.now();
    const cacheKey = `${milkType}_prices`;
    
    if (useCache && fatWisePricesCache && fatWisePricesCache[cacheKey] && (now - lastFatWisePricesUpdate) < CACHE_DURATION) {
      return fatWisePricesCache[cacheKey];
    }
    
    const userId = localStorage.getItem('userId');
    
    if (!userId) {
      // Return empty array for unauthenticated users (like on landing page)
      return [];
    }
    
    const q = query(
      fatWisePricesCollection,
      where('userId', '==', userId),
      where('milkType', '==', milkType)
    );
    const querySnapshot = await getDocs(q);
    
    const prices = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort by fat value
    prices.sort((a, b) => parseFloat(a.fat) - parseFloat(b.fat));
    
    if (!fatWisePricesCache) fatWisePricesCache = {};
    fatWisePricesCache[cacheKey] = prices;
    lastFatWisePricesUpdate = now;
    
    return prices;
  } catch (error) {
    console.error('Error getting fat-wise prices:', error);
    if (fatWisePricesCache && fatWisePricesCache[`${milkType}_prices`]) {
      return fatWisePricesCache[`${milkType}_prices`];
    }
    return [];
  }
};

export const saveFatWisePrice = async (milkType, fat, amount, existingId = null) => {
  try {
    const userId = localStorage.getItem('userId');
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    const priceData = {
      milkType,
      fat: parseFloat(fat),
      amount: parseFloat(amount),
      updatedAt: new Date(),
      userId
    };
    
    if (existingId) {
      // Update existing
      const priceRef = doc(db, 'fatWisePrices', existingId);
      await updateDoc(priceRef, priceData);
      fatWisePricesCache = null;
      return { id: existingId, ...priceData };
    }
    
    // Check if price for this fat already exists
    const q = query(
      fatWisePricesCollection,
      where('userId', '==', userId),
      where('milkType', '==', milkType),
      where('fat', '==', parseFloat(fat))
    );
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.docs.length > 0) {
      // Update existing
      const docId = querySnapshot.docs[0].id;
      const priceRef = doc(db, 'fatWisePrices', docId);
      await updateDoc(priceRef, priceData);
      fatWisePricesCache = null;
      return { id: docId, ...priceData };
    }
    
    // Create new
    priceData.createdAt = new Date();
    const docRef = await addDoc(fatWisePricesCollection, priceData);
    fatWisePricesCache = null;
    
    return { id: docRef.id, ...priceData };
  } catch (error) {
    console.error('Error saving fat-wise price:', error);
    throw error;
  }
};

export const deleteFatWisePrice = async (priceId) => {
  try {
    await deleteDoc(doc(db, 'fatWisePrices', priceId));
    fatWisePricesCache = null;
    return priceId;
  } catch (error) {
    console.error('Error deleting fat-wise price:', error);
    throw error;
  }
};

export const getPriceByFat = async (milkType, fat) => {
  try {
    const prices = await getFatWisePrices(milkType);
    const fatValue = parseFloat(fat);
    
    // Find exact match
    const exactMatch = prices.find(p => Math.abs(p.fat - fatValue) < 0.01);
    if (exactMatch) return exactMatch.amount;
    
    // Find closest lower and higher values for interpolation
    const lower = prices.filter(p => p.fat < fatValue).pop();
    const higher = prices.find(p => p.fat > fatValue);
    
    if (lower && higher) {
      // Linear interpolation
      const ratio = (fatValue - lower.fat) / (higher.fat - lower.fat);
      return lower.amount + ratio * (higher.amount - lower.amount);
    }
    
    if (lower) return lower.amount;
    if (higher) return higher.amount;
    
    return null;
  } catch (error) {
    console.error('Error getting price by fat:', error);
    return null;
  }
};


export const bulkSaveFatWisePrices = async (milkType, entries) => {
  try {
    const userId = localStorage.getItem('userId');
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    // Save all entries
    for (const entry of entries) {
      await saveFatWisePrice(milkType, entry.fat, entry.amount);
    }
    
    // Clear cache after bulk operation
    fatWisePricesCache = null;
    lastFatWisePricesUpdate = 0;
    
    return true;
  } catch (error) {
    console.error('Error bulk saving fat-wise prices:', error);
    throw error;
  }
};
// Public functions for landing page (no authentication required)
export const getPublicFarmers = async (useCache = true) => {
  try {
    console.log('🔍 getPublicFarmers: Starting to fetch farmers...');
    const now = Date.now();
    
    // Return cached data if available and fresh
    if (useCache && publicFarmersCache && (now - lastPublicDataUpdate) < CACHE_DURATION) {
      console.log('📦 getPublicFarmers: Returning cached data:', publicFarmersCache.length, 'farmers');
      return publicFarmersCache;
    }
    
    // Get all farmers without user filtering for landing page display
    console.log('🔥 getPublicFarmers: Querying Firebase...');
    const q = query(farmersCollection, limit(20)); // Limit to 20 for performance
    const querySnapshot = await getDocs(q);
    
    console.log('📊 getPublicFarmers: Firebase query result:', querySnapshot.size, 'documents');
    
    const farmers = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log('👥 getPublicFarmers: Mapped farmers:', farmers);
    
    // Sort by createdAt
    farmers.sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || new Date(0);
      const bTime = b.createdAt?.toDate?.() || new Date(0);
      return aTime - bTime;
    });
    
    publicFarmersCache = farmers;
    lastPublicDataUpdate = now;
    
    console.log('✅ getPublicFarmers: Returning', farmers.length, 'farmers');
    return publicFarmersCache;
  } catch (error) {
    console.error('❌ Error getting public farmers:', error);
    // Return cached data if available, even if stale, or empty array
    if (publicFarmersCache) {
      console.log('📦 getPublicFarmers: Returning cached data due to error');
      return publicFarmersCache;
    }
    console.log('🚫 getPublicFarmers: Returning empty array');
    return [];
  }
};

export const getPublicFatWisePrices = async (milkType, useCache = true) => {
  try {
    console.log('🔍 getPublicFatWisePrices: Starting to fetch prices for', milkType);
    const now = Date.now();
    const cacheKey = `public_${milkType}_prices`;
    
    if (useCache && publicPricesCache && publicPricesCache[cacheKey] && (now - lastPublicDataUpdate) < CACHE_DURATION) {
      console.log('📦 getPublicFatWisePrices: Returning cached data for', milkType);
      return publicPricesCache[cacheKey];
    }
    
    // Get all prices without user filtering for landing page display
    console.log('🔥 getPublicFatWisePrices: Querying Firebase for', milkType, 'prices...');
    const q = query(
      fatWisePricesCollection,
      where('milkType', '==', milkType),
      limit(10) // Limit to 10 for performance
    );
    const querySnapshot = await getDocs(q);
    
    console.log('📊 getPublicFatWisePrices: Firebase query result for', milkType, ':', querySnapshot.size, 'documents');
    
    const prices = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log('💰 getPublicFatWisePrices: Mapped prices for', milkType, ':', prices);
    
    // Sort by fat value
    prices.sort((a, b) => parseFloat(a.fat) - parseFloat(b.fat));
    
    if (!publicPricesCache) publicPricesCache = {};
    publicPricesCache[cacheKey] = prices;
    lastPublicDataUpdate = now;
    
    console.log('✅ getPublicFatWisePrices: Returning', prices.length, 'prices for', milkType);
    return prices;
  } catch (error) {
    console.error('❌ Error getting public fat-wise prices for', milkType, ':', error);
    if (publicPricesCache && publicPricesCache[`public_${milkType}_prices`]) {
      console.log('📦 getPublicFatWisePrices: Returning cached data due to error');
      return publicPricesCache[`public_${milkType}_prices`];
    }
    console.log('🚫 getPublicFatWisePrices: Returning empty array for', milkType);
    return [];
  }
};
// Function to create sample data for landing page demo
export const createSampleDataForLandingPage = async () => {
  try {
    console.log('🚀 Creating sample data for landing page...');
    
    // Create sample farmers
    const sampleFarmers = [
      {
        code: 'DEMO001',
        name: 'Rajesh Kumar',
        mobile: '9876543210',
        village: 'Kakmari',
        userId: 'demo-user-1',
        createdAt: new Date()
      },
      {
        code: 'DEMO002', 
        name: 'Suresh Patil',
        mobile: '9876543211',
        village: 'Athani',
        userId: 'demo-user-2',
        createdAt: new Date()
      },
      {
        code: 'DEMO003',
        name: 'Mahesh Bellagi',
        mobile: '9876543212', 
        village: 'Belagavi',
        userId: 'demo-user-3',
        createdAt: new Date()
      }
    ];

    // Create sample fat-wise prices
    const sampleCowPrices = [
      { milkType: 'cow', fat: 3.0, amount: 28, userId: 'demo-user-1', createdAt: new Date() },
      { milkType: 'cow', fat: 3.5, amount: 30, userId: 'demo-user-1', createdAt: new Date() },
      { milkType: 'cow', fat: 4.0, amount: 32, userId: 'demo-user-1', createdAt: new Date() },
      { milkType: 'cow', fat: 4.5, amount: 34, userId: 'demo-user-1', createdAt: new Date() },
      { milkType: 'cow', fat: 5.0, amount: 36, userId: 'demo-user-1', createdAt: new Date() }
    ];

    const sampleBuffaloPrices = [
      { milkType: 'buffalo', fat: 5.0, amount: 45, userId: 'demo-user-1', createdAt: new Date() },
      { milkType: 'buffalo', fat: 5.5, amount: 48, userId: 'demo-user-1', createdAt: new Date() },
      { milkType: 'buffalo', fat: 6.0, amount: 52, userId: 'demo-user-1', createdAt: new Date() },
      { milkType: 'buffalo', fat: 6.5, amount: 55, userId: 'demo-user-1', createdAt: new Date() },
      { milkType: 'buffalo', fat: 7.0, amount: 58, userId: 'demo-user-1', createdAt: new Date() }
    ];

    console.log('📝 Adding farmers to Firebase...');
    // Add farmers
    for (const farmer of sampleFarmers) {
      const docRef = await addDoc(farmersCollection, farmer);
      console.log('✅ Added farmer:', farmer.name, 'with ID:', docRef.id);
    }

    console.log('💰 Adding prices to Firebase...');
    // Add prices
    for (const price of [...sampleCowPrices, ...sampleBuffaloPrices]) {
      const docRef = await addDoc(fatWisePricesCollection, price);
      console.log('✅ Added price:', price.milkType, 'fat:', price.fat, 'amount:', price.amount, 'with ID:', docRef.id);
    }

    console.log('🎉 Sample data created successfully!');
    
    // Clear cache to force refresh
    publicFarmersCache = null;
    publicPricesCache = null;
    lastPublicDataUpdate = 0;
    
    return true;
  } catch (error) {
    console.error('❌ Error creating sample data:', error);
    return false;
  }
};

// Test function to check Firebase connection and data
export const testLandingPageData = async () => {
  try {
    console.log('🧪 Testing landing page data...');
    
    // Test farmers
    console.log('👥 Testing farmers data...');
    const farmers = await getPublicFarmers(false);
    console.log('Farmers found:', farmers.length);
    
    // Test prices
    console.log('🐄 Testing cow prices...');
    const cowPrices = await getPublicFatWisePrices('cow', false);
    console.log('Cow prices found:', cowPrices.length);
    
    console.log('🐃 Testing buffalo prices...');
    const buffaloPrices = await getPublicFatWisePrices('buffalo', false);
    console.log('Buffalo prices found:', buffaloPrices.length);
    
    const result = {
      farmers: farmers.length,
      cowPrices: cowPrices.length,
      buffaloPrices: buffaloPrices.length,
      totalData: farmers.length + cowPrices.length + buffaloPrices.length
    };
    
    console.log('📊 Test results:', result);
    
    if (result.totalData === 0) {
      console.log('⚠️ No data found. You may want to run createSampleDataForLandingPage()');
    }
    
    return result;
  } catch (error) {
    console.error('❌ Error testing landing page data:', error);
    return { error: error.message };
  }
};